import PropTypes from 'prop-types';
import React from 'react';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import { compose } from 'redux';
import { createStructuredSelector } from 'reselect';
import { BaseComponent } from '../../../components/BaseComponent';
import { HBox, VBox } from '../../../components/Box';
import { Button } from '../../../components/Buttons';
import { Checkbox } from '../../../components/Checkbox';
import { FormField } from '../../../components/FormFields';
import { Times } from '../../../components/Icon';
import { Input } from '../../../components/Input';
import { Modal, ModalControls } from '../../../components/Modal';
import { selectPermissions } from '../../Api/selectors';
import { getVehicleSearchSettings, updateVehicleSearchSettings } from '../actions';
import { selectVehicleFilter } from '../selectors';
import { Permission } from "../../../components/Permission";
import { VEHICLE_SEARCH_SETTING_EDIT, VEHICLE_SEARCH_SETTING_READ } from "../../../components/Permission/constants";

const STATE = {
  edit: false,
  addNewProperty: false,
  newProperty: null,
  propertyForRemove: null,
  items: [],
};
/* eslint-disable react/prefer-stateless-function */
class SearchSettingsComp extends BaseComponent {
  constructor(props) {
    super(props, {
      ...STATE,
    });
  }

  componentDidMount() {
    this.fetchVehicleSearchSettings();
  }

  fetchVehicleSearchSettings() {
    this.props.getVehicleSearchSettings(result => {
      this.setState({ items: result?.searchableProperties ?? [] });
    });
  }

  updateVehicleSearchSettings() {
    const data = {
      type: 'VEHICLE_SEARCH',
      searchableProperties: this.state.items,
    };
    this.props.updateVehicleSearchSettings(data, () => {
      this.setState({ edit: false });
    });
    this.fetchVehicleSearchSettings();
  }

  get items() {
    return this.state.items;
  }

  onOpenEdit() {
    this.setState({ edit: true });
  }

  onCloseEdit() {
    this.setState({ edit: false });
  }

  onToggleFieldStatus(field) {
    return () => {
      this.setState(prev => {
        return {
          ...prev,
          items: prev.items.map(i => {
            if (i.name !== field.name) return i;
            i.enabled = !i.enabled;
            return i;
          }),
        };
      });
    };
  }

  onOpenAdd() {
    this.setState({ addNewProperty: true });
  }

  addNewProperty() {
    this.setState(prev => {
      return {
        addNewProperty: false,
        newProperty: null,
        items: [...prev.items, { name: prev.newProperty, enabled: false }],
      };
    });
  }

  confirmPropertyRemove() {
    this.setState(prev => {
      return {
        propertyForRemove: null,
        items: prev.items.filter(i => i.name !== prev.propertyForRemove),
      };
    });
  }

  cancelPropertyRemove() {
    this.setState({ propertyForRemove: null });
  }

  closeAddNewProperty() {
    this.setState({
      addNewProperty: false,
      newProperty: null,
    });
  }

  renderField(field) {
    return (
      <HBox key={field.name}>
        <div className="name">{field.name}</div>
          <div className="value">
            <Checkbox checked={field.enabled} onChange={this.onToggleFieldStatus(field).bind(this)}>
              {this.lbl('vehicle.settings.search.propertyEnabled')}
            </Checkbox>
          </div>
        <Permission permission={VEHICLE_SEARCH_SETTING_EDIT}>
          <div>
            <Button
              onClick={(() => {
                this.setState({ propertyForRemove: field.name });
              }).bind(this)}
            >
              <Times />
            </Button>
          </div>
        </Permission>
      </HBox>
    );
  }

  renderEdit() {
    return (
      this.state.edit &&
      !this.state.addNewProperty &&
      !this.state.propertyForRemove && (
        <Modal onClose={this.onCloseEdit.bind(this)} title={this.lbl('vehicle.settings.search')}>
          <VBox>{this.items.map(i => this.renderField(i))}</VBox>
          <Permission permission={VEHICLE_SEARCH_SETTING_EDIT}>
            <Button small onClick={this.onOpenAdd.bind(this)}>
              +
            </Button>
          </Permission>
          <VBox>
            * {this.lbl('vehicle.settings.search.tip')}
          </VBox>
          <Permission permission={VEHICLE_SEARCH_SETTING_EDIT}>
            <ModalControls>
              <Button
                isPrimary
                onClick={this.updateVehicleSearchSettings.bind(this)}
              >
                {this.lbl('confirm')}
              </Button>
              <Button onClick={this.onCloseEdit.bind(this)}>{this.lbl('cancel')}</Button>
            </ModalControls>
          </Permission>
        </Modal>
      )
    );
  }

  renderAddNewProperty() {
    return (
      this.state.addNewProperty && (
        <Modal onClose={this.closeAddNewProperty.bind(this)} title={this.lbl('vehicle.settings.search.addNewPropertyModal')}>
          <FormField>
            <Input name="newProperty" value={this.state.newProperty ?? ''} onChange={this.onInputChange.bind(this)} />
          </FormField>
          <ModalControls>
            <Button primary disabled={!this.state.newProperty} onClick={this.addNewProperty.bind(this)}>
              {this.lbl('confirm')}
            </Button>
            <Button onClick={this.closeAddNewProperty.bind(this)}>{this.lbl('cancel')}</Button>
          </ModalControls>
        </Modal>
      )
    );
  }

  renderRemoveProperty() {
    return (
      this.state.propertyForRemove && (
        <Modal onClose={this.closeAddNewProperty.bind(this)} title={this.lbl('vehicle.settings.search.removePropertyModal')}>
          <ModalControls>
            <Button primary onClick={this.confirmPropertyRemove.bind(this)}>
              {this.lbl('confirm')}
            </Button>
            <Button onClick={this.cancelPropertyRemove.bind(this)}>{this.lbl('cancel')}</Button>
          </ModalControls>
        </Modal>
      )
    );
  }

  render() {
    return (
        <Permission permission={VEHICLE_SEARCH_SETTING_READ}>
          <div>
            <div onClick={this.onOpenEdit.bind(this)}> Vehicle search settings </div>
            {this.renderEdit()}
            {this.renderAddNewProperty()}
            {this.renderRemoveProperty()}
          </div>
        </Permission>
    );
  }
}

SearchSettingsComp.propTypes = {
  dispatch: PropTypes.func.isRequired,
};

const mapStateToProps = (state, ownProps) =>
  createStructuredSelector({
    filter: selectVehicleFilter(),
    permissions: selectPermissions(),
  });

function mapDispatchToProps(dispatch, ownProps) {
  return {
    dispatch,
    getVehicleSearchSettings(callback) {
      dispatch(getVehicleSearchSettings(callback));
    },
    updateVehicleSearchSettings(data, callback) {
      dispatch(updateVehicleSearchSettings(data, callback));
    },
  };
}

const withConnect = connect(
  mapStateToProps,
  mapDispatchToProps,
);

export const SearchSettings = compose(
  withConnect,
  withRouter,
)(SearchSettingsComp);
